import { Separator } from '@/components/ui/separator';
import { Check, Download } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import clsx from 'clsx';
import { formatDate, getErrToast } from '@/utils/helpers';
import AxiosInstance from '@/utils/AxiosInstance';
import { downloadBackendPdf } from '@/utils/downloadBackendPdf';

const ViewTreatmentPlan = ({ treatmentPlan }: any) => {
  console.log({ treatmentPlan });
  const [isDownloading, setIsDownloading] = useState(false);
  const [fetchedOrgName, setFetchedOrgName] = useState<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const orgIdForFetch = useMemo(() => {
    const patient = treatmentPlan?.patientId;
    if (!patient) return null;
    const org = patient.organizationId;
    if (typeof org === 'string' && org.trim() !== '') return org.trim();
    if (org && typeof org === 'object' && org !== null) {
      const hasName =
        (org.organizationName != null &&
          String(org.organizationName).trim() !== '') ||
        (org.name != null && String(org.name).trim() !== '');
      if (hasName) return null;
      const id = org._id;
      if (id != null && String(id).trim() !== '') return String(id).trim();
    }
    return null;
  }, [treatmentPlan?.patientId]);

  useEffect(() => {
    setFetchedOrgName(null);
    if (!orgIdForFetch) return;
    let cancelled = false;
    AxiosInstance()
      .get<{ data?: { organizationName?: string; name?: string } }>(
        `/organizations/${orgIdForFetch}`,
      )
      .then((res) => {
        const d = res.data?.data;
        const name = d?.organizationName ?? d?.name;
        if (
          !cancelled &&
          name != null &&
          String(name).trim() !== ''
        ) {
          setFetchedOrgName(String(name).trim());
        }
      })
      .catch(() => {
        if (!cancelled) setFetchedOrgName(null);
      });
    return () => {
      cancelled = true;
    };
  }, [orgIdForFetch]);

  const organizationDisplay = useMemo(() => {
    const patient = treatmentPlan?.patientId;
    if (!patient || typeof patient !== 'object') return '—';
    if (fetchedOrgName) return fetchedOrgName;

    const nameFromOrg = (o: unknown): string | null => {
      if (!o || typeof o !== 'object') return null;
      const rec = o as Record<string, unknown>;
      const n = rec.organizationName ?? rec.name;
      if (n == null || String(n).trim() === '') return null;
      return String(n).trim();
    };

    const fromPatientOrg = nameFromOrg(patient.organizationId);
    if (fromPatientOrg) return fromPatientOrg;

    const fromPatientAlt = nameFromOrg(patient.organization);
    if (fromPatientAlt) return fromPatientAlt;

    const fromPlanOrg = nameFromOrg(treatmentPlan?.organizationId);
    if (fromPlanOrg) return fromPlanOrg;

    const fac = patient.facilityId;
    if (fac && typeof fac === 'object') {
      const fn = (fac as { facilityName?: string; name?: string }).facilityName ??
        (fac as { facilityName?: string; name?: string }).name;
      if (fn != null && String(fn).trim() !== '') return String(fn).trim();
    }

    return '—';
  }, [treatmentPlan?.patientId, treatmentPlan?.organizationId, fetchedOrgName]);

  if (!treatmentPlan) {
    return <div className="text-muted-foreground p-4">No treatment plan data to display.</div>;
  }

  const icdCodesDisplay = (() => {
    const raw = treatmentPlan.icdCodes;
    const fromArray = Array.isArray(raw)
      ? raw.filter((c: unknown) => c != null && String(c).trim() !== '').map((c: unknown) => String(c).trim())
      : raw != null && String(raw).trim() !== ''
        ? [String(raw).trim()]
        : [];
    if (fromArray.length > 0) return fromArray.join(', ');
    const single = treatmentPlan.icdCode;
    if (single != null && String(single).trim() !== '') return String(single).trim();
    return '';
  })();

  const patient = typeof treatmentPlan.patientId === 'object' ? treatmentPlan.patientId : {};
  const caretaker = typeof treatmentPlan.careTakerId === 'object' ? treatmentPlan.careTakerId : {};
  const planDate = treatmentPlan.createdAt || treatmentPlan.treatmentPlanDate || new Date().toISOString();
  const generatedDate = new Date(planDate).toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric'
  });
  const formattedDate = generatedDate.replace(/\//g, '-');
  const dob = patient?.dateOfBirth ? formatDate(patient.dateOfBirth) : '—';
  const patientName = [patient?.firstName, patient?.middleName, patient?.lastName].filter(Boolean).join(' ') || '—';
  const caretakerName = [caretaker?.firstName, caretaker?.middleName, caretaker?.lastName].filter(Boolean).join(' ') || '—';
  const categories: string[] = [];
  const catSet = new Set<string>();
  const questions = Array.isArray(treatmentPlan?.questions) ? treatmentPlan.questions : [];
  questions.forEach((q: any) => {
    if (q?.category && !catSet.has(q.category)) {
      catSet.add(q.category);
      categories.push(q.category);
    }
  });
  const formatAnswerDisplay = (q: any) => {
    const raw = q?.answer;
    if (raw == null || (typeof raw === 'string' && raw.trim() === '')) return raw;
    if (q?.type === 'date') return formatDate(String(raw));
    return raw;
  };

  const renderQuestion = (q: any) => {
    // Check if answer doesn't match any option (meaning "other" was selected with custom text)
    const hasOptions = q.options && Array.isArray(q.options) && q.options.length > 0 && q.options.some((opt: string) => opt.trim() !== '');
    let isOtherSelected = false;
    let customAnswer = '';
    
    if (hasOptions && q.answer) {
      const validOptions = q.options.filter((opt: string) => opt.trim() !== '').map((opt: string) => opt.trim());
      
      if (q.type === 'multiple_choice') {
        if (Array.isArray(q.answer)) {
          // Check if any answer doesn't match options
          const nonMatching = q.answer.filter((ans: any) => !validOptions.includes(String(ans).trim()));
          if (nonMatching.length > 0) {
            isOtherSelected = true;
            customAnswer = nonMatching.map((ans: any) => String(ans).trim()).join(', ');
          }
        } else {
          // Answer is a string but type is multiple_choice (custom text entered)
          const answerStr = String(q.answer).trim();
          if (answerStr && !validOptions.includes(answerStr)) {
            isOtherSelected = true;
            customAnswer = answerStr;
          }
        }
      } else if (q.type === 'single_choice' || !q.type) {
        // For single choice, check if answer doesn't match any option
        const answerStr = String(q.answer).trim();
        if (answerStr && !validOptions.includes(answerStr)) {
          isOtherSelected = true;
          customAnswer = answerStr;
        }
      }
    }
    
    // Check if "other" option exists in the options
    const otherOptionExists = hasOptions && q.options.some((opt: string) => opt.trim().toLowerCase() === 'other');
    
    return (
      <div key={q.questionrandomid} className="mb-4">
        <div className="teachers text-xs mb-2.5">{q.question}</div>
        {hasOptions ? (
          <div className=" space-y-1">
            {q.options
              .filter((opt: string) => opt.trim() !== '')
              .map((opt: string, idx: number) => {
                const optTrimmed = opt.trim();
                const isSelected =
                  q.type === 'multiple_choice'
                    ? Array.isArray(q.answer) && q.answer.some((ans: any) => String(ans).trim() === optTrimmed)
                    : String(q.answer).trim() === optTrimmed;
                
                // Mark "other" as selected if custom answer was entered
                const isOtherOpt = optTrimmed.toLowerCase() === 'other';
                const showAsSelected = isSelected || (isOtherOpt && isOtherSelected);
                
                return (
                  <div key={idx} className="flex items-center">
                    <span
                      className={` font-medium teachers text-xs ${
                        showAsSelected ? '' : ''
                      }`}
                    >
                      {opt}
                    </span>
                    {showAsSelected && <span className="ml-2 text-green-500 text-xs"><Check width={14} height={14}/></span>}
                  </div>
                );
              })}
            {/* Show "other" option if it doesn't exist in options but custom answer was entered */}
            {isOtherSelected && !otherOptionExists && (
              <div className="flex items-center">
                <span className="font-medium teachers text-xs">Other</span>
                <span className="ml-2 text-green-500 text-xs"><Check width={14} height={14}/></span>
              </div>
            )}
            {/* Show custom answer text if "other" was selected */}
            {isOtherSelected && customAnswer && (
              <div className="ml-6 mt-1">
                <span className="font-medium teachers text-xs text-gray-600 italic">
                  {customAnswer}
                </span>
              </div>
            )}
          </div>
        ) : (
          q.answer && (
            <div className="font-medium teachers text-xs ">
              {formatAnswerDisplay(q)}
            </div>
          )
        )}
        <Separator className='bg-[#dddddd] my-2'/>
      </div>
    );
  };
  const sectionTitle = (cat: string) => {
    return cat
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (l: string) => l.toUpperCase());
  };
  const handleDownloadPdf = async () => {
    const id = treatmentPlan?._id;
    if (!id) return;
    setIsDownloading(true);
    try {
      await downloadBackendPdf({
        url: `/treatment-plan/${id}/pdf`,
        fallbackFilename: 'treatment-plan.pdf',
      });
    } catch (err) {
      getErrToast(err);
    } finally {
      setIsDownloading(false);
    }
  };
  return (
    <div className="bg-gray-100 min-h-screen py-8 px-4">
        <div className='flex justify-end max-w-[595px] mx-auto mb-4'>

      <Button
        variant="outline"
        id='pdfDownload'
        className="w-full sm:w-auto hidden"
        onClick={handleDownloadPdf}
        disabled={isDownloading}
        >
        <Download className="w-4 h-4 mr-2" />
        {isDownloading ? "Preparing PDF..." : "PDF"}
      </Button>
          </div>
      <div ref={contentRef} className={clsx ("bg-white shadow-lg p-2 md:p-10")} style={{ 
        width: '595px', 
        maxWidth: '100%', 
        margin: '0 auto', 
        boxSizing: 'border-box',
        // padding: '40px',
        minHeight: '842px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
      }}>
        <div className="header mb-8 bg-[#eeeeee] p-5 rounded-md ">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <span className="font-semibold text-[#6E6E6E] teachers text-sm">PATIENT NAME:</span>
              <span className="ml-2 text-black text-sm teachers">{patientName}</span>
            </div>
            <div>
              <span className="font-semibold text-[#6E6E6E] teachers text-sm">DATE OF BIRTH:</span>
              <span className="ml-2 text-black text-sm teachers">{dob}</span>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <span className="font-semibold text-[#6E6E6E] teachers text-sm">CARETAKER:</span>
              <span className="ml-2 text-black text-sm teachers">{caretakerName}</span>
            </div>
            <div>
              <span className="font-semibold text-[#6E6E6E] teachers text-sm">Creation Date:</span>
              <span className="ml-2 text-black text-sm teachers">{generatedDate}</span>
            </div>
          </div>

          <div className="mt-4 pt-3 border-t border-[#dddddd] space-y-3">
            {icdCodesDisplay && false ? (
              <div>
                <span className="font-semibold text-[#6E6E6E] teachers text-sm">ICD CODES:</span>
                <span className="ml-2 text-black text-sm teachers">{icdCodesDisplay}</span>
              </div>
            ) : null}
            <div>
              <span className="font-semibold text-[#6E6E6E] teachers text-sm">ORGANIZATION:</span>
              <span className="ml-2 text-black text-sm teachers">{organizationDisplay}</span>
            </div>
          </div>
        </div>
        {categories.map((cat) => {
          const sectionQuestions = questions.filter((q: any) => q.category === cat);
          return (
            <div key={cat} className="mb-3">
              <h3 className="font-medium teachers mb-2.5">
                {sectionTitle(cat)}
              </h3>
              {sectionQuestions.map(renderQuestion)}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ViewTreatmentPlan;

